/**
 * The Cortex Console Agents board (harness row CC1B-12; spec.md 8A ViewAgentsBoard,
 * requirements.md FR-14).
 *
 * What this component does: render the answer `GET /api/cortex/console/agents` gave -
 * the columns it names, the cards it built, the scope it says it applied, and the
 * thresholds it decided liveness by.
 *
 * What it deliberately does NOT do:
 *
 * - decide who may see a session. Eligibility was applied once by the read layer;
 *   a card that arrives here is one the caller is entitled to;
 * - decide which column a session belongs in, or whether it is live. Both come from
 *   the server (`column`, `liveness`), so the board and the Overview counts cannot
 *   drift apart;
 * - start, stop or relaunch anything. There is no control on this board (spec.md 8C
 *   row NR-2); the "All streams" switch is a scope, not an action;
 * - invent a number. A session that reported no row denominator shows "not reported",
 *   never 0 of 0.
 */
import React from "react";
import { ContentRegionLoader } from "../components/loading";
import { KanbanBoard, KanbanCard, KanbanColumn, kanbanStyles } from "../components/kanban";
import type {
  ConsoleAgentCard,
  ConsoleAgentsBoardResponse,
  ConsoleAgentsScope,
  ConsoleRefusal,
} from "../api/types";
import { idLabel } from "../labels/displayLabels";
import styles from "./AgentsBlade.module.css";

/**
 * The words a person reads for each column the server names. The server owns the
 * column SET (it sends `column_names`); this map only supplies English for the ids it
 * sends, and an id with no entry is still shown, titled from the id itself through the
 * display-label mapping - so a column this SPA has not met can never go missing from
 * the board.
 */
const COLUMN_LABELS: Record<string, string> = {
  running: "Running",
  decision_required: "Decision required",
  agent_blocked: "Agent blocked",
  ready_for_test: "Ready for test",
  complete: "Complete",
};

const LIVENESS_LABELS: Record<string, string> = {
  live: "Live",
  silent: "Silent",
  abandoned: "Abandoned",
};

export function columnLabel(columnId: string): string {
  return idLabel(columnId, COLUMN_LABELS);
}

export function livenessLabel(liveness: string): string {
  return idLabel(liveness, LIVENESS_LABELS);
}

/**
 * A threshold the server reports in seconds, in the words a person uses for it.
 * Presentation only: the number is the server's, and nothing here is compared
 * against a clock (ASDM section 13 plain language).
 */
export function plainDuration(seconds: number): string {
  const units: Array<[number, string, string]> = [
    [86400, "day", "days"],
    [3600, "hour", "hours"],
    [60, "minute", "minutes"],
  ];
  for (const [size, one, many] of units) {
    if (seconds >= size && seconds % size === 0) {
      const count = seconds / size;
      return `${count} ${count === 1 ? one : many}`;
    }
  }
  return `${seconds} seconds`;
}

/** "9 of 14", or the plain truth when the session never reported a denominator. */
export function rowsLine(card: Pick<ConsoleAgentCard, "rows_passed" | "rows_total">): string {
  if (card.rows_passed === null || card.rows_passed === undefined) {
    return "Rows: not reported";
  }
  if (card.rows_total === null || card.rows_total === undefined) {
    return `Rows: ${card.rows_passed} passed, total not reported`;
  }
  return `Rows: ${card.rows_passed} of ${card.rows_total}`;
}

/**
 * "Complete: closed out on <date> (now at <path>)", or "(location not
 * reported)" when the closed-out row carried no path (requirements.md FR-12).
 * Only rendered for a card whose `package_closed_out` is true.
 */
export function completeLine(
  card: Pick<ConsoleAgentCard, "closed_out_at" | "closed_out_package_rel">,
): string {
  const location = card.closed_out_package_rel
    ? `now at ${card.closed_out_package_rel}`
    : "location not reported";
  return `Complete: closed out on ${card.closed_out_at || "not reported"} (${location})`;
}

export function isBoard(
  body: ConsoleAgentsBoardResponse | ConsoleRefusal,
): body is ConsoleAgentsBoardResponse {
  return Boolean((body as ConsoleAgentsBoardResponse).columns);
}

export type AgentsBladeProps = {
  board: ConsoleAgentsBoardResponse | ConsoleRefusal | null;
  loading: boolean;
  /** The scope the person asked for. What was applied is on the answer itself. */
  requestedScope: ConsoleAgentsScope;
  onScopeChange: (scope: ConsoleAgentsScope) => void;
  onRefresh: () => void;
};

function AgentCard({ card }: { card: ConsoleAgentCard }) {
  return (
    <article data-testid={`agent-card-${card.session_id}`}>
      <h4 className={styles.cardTitle}>{card.package_rel || card.session_id}</h4>
      <p className={styles.cardLine} data-testid="agent-card-rows">
        {rowsLine(card)}
      </p>
      <p className={styles.cardLine}>Machine: {card.machine_id || "not reported"}</p>
      <p className={styles.cardLine}>
        Sponsor: {card.sponsor_identity || "not reported"}
      </p>
      <p className={styles.cardLine}>
        Last heard: {card.last_heartbeat_at || "not reported"}{" "}
        <span className={styles.liveness} data-testid="agent-card-liveness">
          {card.package_closed_out ? "Complete" : livenessLabel(card.liveness)}
        </span>
      </p>
      {card.package_closed_out ? (
        <p className={styles.cardLine} data-testid="agent-card-complete">
          {completeLine(card)}
        </p>
      ) : null}
      {card.blocked_on ? (
        <p
          className={`${styles.cardLine} ${styles.blockedOn}`}
          data-testid="agent-card-blocked-on"
        >
          Blocked on: {card.blocked_on}
        </p>
      ) : null}
      {card.column === "agent_blocked" && card.quiet_reason?.trim() ? (
        <details className={styles.detail} data-testid="agent-card-quiet-reason">
          <summary className={styles.detailSummary}>{card.quiet_reason}</summary>
          <p className={styles.cardLine} data-testid="agent-card-quiet-reason-body">
            {card.quiet_reason_detail?.trim()
              ? card.quiet_reason_detail
              : "No further detail was given."}
          </p>
        </details>
      ) : null}
      <details className={styles.detail}>
        <summary className={styles.detailSummary}>Latest heartbeat</summary>
        {card.package_closed_out ? (
          <p className={styles.cardLine} data-testid="agent-card-raw-liveness">
            Liveness: {livenessLabel(card.liveness)}
          </p>
        ) : null}
        <p className={styles.cardLine}>Session: {card.session_id}</p>
        <p className={styles.cardLine}>Stream: {card.stream || "not reported"}</p>
        <p className={styles.cardLine}>Branch: {card.branch || "not reported"}</p>
        <p className={styles.cardLine}>
          State: {idLabel(card.phase)}
          {card.iteration === null || card.iteration === undefined
            ? ""
            : `, iteration ${card.iteration}`}
        </p>
        <p className={styles.cardLine}>
          First unpassed row: {card.first_unpassed_row || "none reported"}
        </p>
        <p className={styles.cardLine}>
          Last commit: {card.last_commit || "not reported"}
          {card.behind_origin_tagd === null || card.behind_origin_tagd === undefined
            ? ""
            : `, ${card.behind_origin_tagd} behind the integration branch`}
        </p>
      </details>
    </article>
  );
}

export function AgentsBlade({
  board,
  loading,
  requestedScope,
  onScopeChange,
  onRefresh,
}: AgentsBladeProps) {
  const answered = board && isBoard(board) ? board : null;
  const message = board?.message;

  return (
    <div className={styles.blade} data-testid="console-agents-blade">
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Agents</h2>
          {answered ? (
            <p className={styles.scopeStatement} data-testid="agents-scope-statement">
              {answered.scope_statement}
            </p>
          ) : null}
        </div>
        <div className={styles.controls}>
          <label className={styles.switch}>
            <input
              type="checkbox"
              data-testid="agents-all-streams-switch"
              checked={requestedScope === "all_streams"}
              onChange={(event) =>
                onScopeChange(event.target.checked ? "all_streams" : "granted_streams")
              }
            />
            All streams
          </label>
          <button
            type="button"
            className={styles.refresh}
            data-testid="agents-refresh"
            onClick={onRefresh}
          >
            Refresh
          </button>
        </div>
      </div>

      {message ? (
        <p className={styles.message} data-testid="agents-message">
          {message}
        </p>
      ) : null}

      {loading ? (
        <ContentRegionLoader
          label="Loading the Agents board"
          data-testid="agents-loading"
        />
      ) : null}

      {!loading && answered ? (
        <>
          <KanbanBoard columnsTestId="agents-columns">
            {answered.column_names.map((columnId) => {
              const cards = answered.columns[columnId] || [];
              return (
                <section
                  key={columnId}
                  className={kanbanStyles.column}
                  data-testid={`agents-column-${columnId}`}
                >
                  <KanbanColumn title={columnLabel(columnId)} count={cards.length}>
                    {cards.map((card) => (
                      <KanbanCard key={card.session_id} board="agents" status={columnId}>
                        <AgentCard card={card} />
                      </KanbanCard>
                    ))}
                  </KanbanColumn>
                </section>
              );
            })}
          </KanbanBoard>
          <p className={styles.thresholds} data-testid="agents-thresholds">
            A loop is called silent after{" "}
            {plainDuration(answered.thresholds.loop_silent_after_seconds)} without a
            heartbeat, a coding session after{" "}
            {plainDuration(answered.thresholds.session_silent_after_seconds)}.
          </p>
        </>
      ) : null}
    </div>
  );
}
